// TMS API Configuration
const TMS_API_URL = 'https://staging-api.shypv.com/shypv/order/createOrder';

interface DimWeight {
  len: number;
  widt: number;
  heght: number;
  wght: number;
}

interface PickupLocation {
  v2: boolean;
  add: string;
  tim: string;
  ph: string;
  jd: string;
  name: string;
  isclct: boolean;
  addtl: string;
  slot: string;
  cnam: string;
  hbid: string;
  datechng: boolean;
  citychng: boolean;
  isNextDay: boolean;
}

interface DeliveryLocation {
  v2: boolean;
  add: string;
  tim: string;
  ph: string;
  jd: string;
  name: string;
  email: string;
  ccamt: number;
  isclct: boolean;
  addtl: string;
  bkref: string;
  slot: string;
  cnam: string;
  noitms: number;
  datechng: boolean;
  citychng: boolean;
  area_name: string;
  city: string;
  dimwghts: DimWeight[];
  isNextDay: boolean;
  serviceType: string;
}

interface TMSOrderRequest {
  calculatePrice: boolean;
  v2api: boolean;
  autoScheduleBooking: boolean;
  noCod: boolean;
  return: boolean;
  mpmd: boolean;
  prid: number;
  pkpcnt: number;
  dlvcnt: number;
  tamt: number;
  ptyp: string;
  cur: string;
  chnl: string;
  vtyp: string;
  bktyp: string;
  dom: string;
  paytyp: string;
  pkp: PickupLocation[];
  dlv: DeliveryLocation[];
  bkpage: string;
  apptyp: string;
  pkphb: boolean;
  migr: boolean;
  ispr: boolean;
}

interface TMSOrderResponse {
  data: {
    bkrefid: string;
    bkstat: string;
  };
  info: any;
  errors: any[];
  success: boolean;
  code: string | null;
  message: string | null;
}

interface BookingData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  pickupCountry: string;
  pickupCity: string;
  pickupAddress: string;
  pickupAddressLine2?: string;
  dropoffCountry: string;
  dropoffCity: string;
  dropoffAddress: string;
  dropoffAddressLine2?: string;
  packageType: string;
  weight: number;
  weightUnit: string;
  length?: number;
  width?: number;
  height?: number;
  packageDescription: string;
  pickupDate: string;
  timeSlot: string;
  deliveryDate: string;
  deliveryTimeSlot: string;
  totalAmount: number;
  bookingNumber?: string;
}

export const tmsService = {
  /**
   * Format date and time for TMS API
   */
  formatDateTime(date: string, timeSlot: string): string {
    // Convert date string to YYYY-MM-DD HH:MM:SS format
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    // Map time slot to time
    const timeMap: Record<string, string> = {
      '8-11': '09:00:00',
      '11-2': '12:00:00',
      '2-5': '15:00:00',
      '5-8': '18:00:00'
    };
    
    const time = timeMap[timeSlot] || '14:00:00';
    
    return `${year}-${month}-${day} ${time}`;
  },

  /**
   * Get slot display text
   */
  getSlotDisplay(timeSlot: string): string {
    // Map time slots to display format
    const slotMap: Record<string, string> = {
      '8-11': '8AM - 11AM',
      '11-2': '11AM - 2PM',
      '2-5': '2PM - 5PM',
      '5-8': '5PM - 8PM'
    };
    
    return slotMap[timeSlot] || '9AM - 6PM';
  },

  /**
   * Get country domain code
   */
  getCountryDomain(country: string): string {
    // Extract country code or use default
    if (country.includes('Emirates') || country.includes('Dubai') || country.includes('UAE')) {
      return 'AE';
    }
    return 'AE'; // Default to AE
  },

  /**
   * Create TMS order
   */
  async createTMSOrder(bookingData: BookingData): Promise<TMSOrderResponse> {
    try {
      console.log('Creating TMS order with booking data:', bookingData);

      // Format pickup datetime
      const pickupDateTime = this.formatDateTime(bookingData.pickupDate, bookingData.timeSlot);
      const deliveryDateTime = this.formatDateTime(bookingData.deliveryDate, bookingData.deliveryTimeSlot);

      // Prepare TMS order request
      const tmsRequest: TMSOrderRequest = {
        calculatePrice: false,
        v2api: false,
        autoScheduleBooking: false,
        noCod: false,
        return: false,
        mpmd: false,
        prid: 17,
        pkpcnt: 1,
        dlvcnt: 1,
        tamt: bookingData.totalAmount,
        ptyp: 'delivery',
        cur: 'AED',
        chnl: 'B2C',
        vtyp: 'Van',
        bktyp: 'Next Day',
        dom: this.getCountryDomain(bookingData.pickupCountry),
        paytyp: 'PREPAID',
        pkp: [
          {
            v2: false,
            add: `${bookingData.pickupAddress} ${bookingData.pickupAddressLine2 || ''}`,
            tim: pickupDateTime,
            ph: bookingData.customerPhone,
            jd: '',
            name: bookingData.customerName,
            isclct: false,
            addtl: bookingData.pickupAddressLine2 || '',
            slot: this.getSlotDisplay(bookingData.timeSlot),
            cnam: bookingData.pickupCity,
            hbid: 'bullet-delivery',
            datechng: false,
            citychng: false,
            isNextDay: false
          }
        ],
        dlv: [
          {
            v2: false,
            add: `${bookingData.dropoffAddress} ${bookingData.dropoffAddressLine2 || ''}`,
            tim: deliveryDateTime,
            ph: bookingData.customerPhone,
            jd: bookingData.packageDescription,
            name: bookingData.customerName,
            email: bookingData.customerEmail,
            ccamt: 0.0,
            isclct: false,
            addtl: bookingData.dropoffAddressLine2 || '',
            bkref: bookingData.bookingNumber || `BUL-${Date.now()}`,
            slot: this.getSlotDisplay(bookingData.deliveryTimeSlot),
            cnam: bookingData.dropoffCity,
            noitms: 1,
            datechng: false,
            citychng: false,
            area_name: ' ',
            city: bookingData.dropoffCity,
            dimwghts: [
              {
                len: bookingData.length || 1.0,
                widt: bookingData.width || 1.0,
                heght: bookingData.height || 1.0,
                wght: bookingData.weight
              }
            ],
            isNextDay: false,
            serviceType: 'Timexpress same day Service'
          }
        ],
        bkpage: 'dlvform',
        apptyp: 'API_PARTNERS',
        pkphb: false,
        migr: false,
        ispr: false
      };

      console.log('TMS API request:', JSON.stringify(tmsRequest, null, 2));

      // Call TMS API directly
      const response = await fetch(TMS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tmsRequest)
      });

      if (!response.ok) {
        throw new Error(`TMS API request failed with status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('TMS API response:', responseData);

      if (responseData.success) {
        return responseData as TMSOrderResponse;
      } else {
        throw new Error(responseData.message || 'Failed to create TMS order');
      }
    } catch (error) {
      console.error('Error creating TMS order:', error);
      throw error;
    }
  }
};

export default tmsService;

